import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const BUSINESS_TYPES = [
    { key: "residential_builder", label: "Residential Builder" },
    { key: "commercial_contractor", label: "Commercial Contractor" },
    { key: "developer", label: "Real Estate Developer" },
    { key: "investor", label: "Investor / Fix-and-Flip" },
    { key: "mixed", label: "Mixed" },
];

const PROJECT_TYPES = [
    { key: "custom_homes", label: "Custom Homes" },
    { key: "spec_homes", label: "Spec Homes" },
    { key: "multi_family", label: "Multi-family" },
    { key: "fix_flip", label: "Fix & Flip" },
    { key: "rental", label: "Rental / BRRRR" },
    { key: "commercial", label: "Commercial" },
    { key: "ground_up_residential", label: "Ground-up Residential" },
    { key: "land_development", label: "Land Development" },
    { key: "tax_deed", label: "Tax-Deed / Tax-Lien" },
];

const TEAM_SIZES = ["1", "2-10", "11-50", "51+"];

export default function Onboarding() {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        name: "",
        business_type: "",
        project_types: [],
        team_size: "",
        service_area: "",
        currency: "USD",
        measurement_system: "imperial",
        logo_url: "",
        workspace_choice: "clean",
    });
    const [saving, setSaving] = useState(false);
    const { refresh } = useAuth();
    const { reloadOrg } = useBranding();
    const nav = useNavigate();
    const total = 6;

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const toggleProjectType = (k) => setForm((s) => ({
        ...s,
        project_types: s.project_types.includes(k) ? s.project_types.filter((x) => x !== k) : [...s.project_types, k],
    }));

    const back = () => setStep((s) => Math.max(1, s - 1));
    const next = () => setStep((s) => Math.min(total, s + 1));

    const canProceed = () => {
        if (step === 1) return !!form.business_type;
        if (step === 2) return form.project_types.length > 0;
        if (step === 3) return !!form.team_size;
        if (step === 4) return !!form.service_area;
        if (step === 5) return !!form.currency && !!form.measurement_system;
        if (step === 6) return !!form.name;
        return true;
    };

    const submit = async () => {
        setSaving(true);
        try {
            const { data } = await api.post("/organizations", {
                name: form.name,
                business_type: form.business_type,
                project_types: form.project_types,
                team_size: form.team_size,
                service_area: form.service_area,
                currency: form.currency,
                measurement_system: form.measurement_system,
                logo_url: form.logo_url || null,
            });
            // relogin to pick up active_org_id
            const rt = localStorage.getItem("bdo_refresh");
            if (rt) {
                try {
                    const r = await api.post("/auth/refresh", { refresh_token: rt });
                    localStorage.setItem("bdo_access", r.data.access_token);
                    localStorage.setItem("bdo_refresh", r.data.refresh_token);
                } catch { /* ignore */ }
            }
            await refresh();
            await reloadOrg();
            toast.success(`Organization created — ${data.name}`);
            nav("/app/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create organization");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="page-onboarding">
            <div className="border-b border-border px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="h-5 w-5 bg-navy" />
                    <span className="text-sm font-medium tracking-tight">The Dream Forge · ForgeOS</span>
                </div>
                <div className="text-xs text-muted-foreground">Step {step} of {total}</div>
            </div>
            <div className="h-0.5 bg-secondary">
                <div className="h-full bg-navy transition-all" style={{ width: `${(step / total) * 100}%` }} data-testid="onboarding-progress" />
            </div>

            <div className="max-w-2xl mx-auto p-8 md:p-12">
                {step === 1 && (
                    <div data-testid="step-business-type">
                        <div className="overline">Step 1</div>
                        <h1 className="mt-3 font-display text-4xl tracking-tight">What kind of business do you run?</h1>
                        <p className="mt-3 text-sm text-muted-foreground">Pick the closest fit — you can add more later.</p>
                        <RadioGroup value={form.business_type} onValueChange={(v) => set("business_type", v)} className="mt-8 grid gap-3">
                            {BUSINESS_TYPES.map((b) => (
                                <label key={b.key} className={`flex items-center gap-3 p-4 border cursor-pointer ${form.business_type === b.key ? "border-foreground bg-secondary/50" : "border-border"}`} data-testid={`biz-${b.key}`}>
                                    <RadioGroupItem value={b.key} />
                                    <span>{b.label}</span>
                                </label>
                            ))}
                        </RadioGroup>
                    </div>
                )}
                {step === 2 && (
                    <div data-testid="step-project-types">
                        <div className="overline">Step 2</div>
                        <h1 className="mt-3 font-display text-4xl tracking-tight">Which project types?</h1>
                        <p className="mt-3 text-sm text-muted-foreground">Select all that apply.</p>
                        <div className="mt-8 grid md:grid-cols-2 gap-3">
                            {PROJECT_TYPES.map((p) => {
                                const checked = form.project_types.includes(p.key);
                                return (
                                    <label key={p.key} className={`flex items-center gap-3 p-4 border cursor-pointer ${checked ? "border-foreground bg-secondary/50" : "border-border"}`} data-testid={`ptype-${p.key}`}>
                                        <Checkbox checked={checked} onCheckedChange={() => toggleProjectType(p.key)} />
                                        <span>{p.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}
                {step === 3 && (
                    <div data-testid="step-team-size">
                        <div className="overline">Step 3</div>
                        <h1 className="mt-3 font-display text-4xl tracking-tight">How large is your team?</h1>
                        <RadioGroup value={form.team_size} onValueChange={(v) => set("team_size", v)} className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
                            {TEAM_SIZES.map((t) => (
                                <label key={t} className={`flex items-center justify-center p-4 border cursor-pointer ${form.team_size === t ? "border-foreground bg-secondary/50" : "border-border"}`} data-testid={`team-${t}`}>
                                    <RadioGroupItem value={t} className="hidden" />
                                    <span>{t}</span>
                                </label>
                            ))}
                        </RadioGroup>
                    </div>
                )}
                {step === 4 && (
                    <div data-testid="step-service-area">
                        <div className="overline">Step 4</div>
                        <h1 className="mt-3 font-display text-4xl tracking-tight">Where do you work?</h1>
                        <p className="mt-3 text-sm text-muted-foreground">Primary service area — state and counties or metro area.</p>
                        <Input value={form.service_area} onChange={(e) => set("service_area", e.target.value)} placeholder="e.g. Colorado — Front Range (Denver, Boulder, Fort Collins)" className="mt-6" data-testid="input-service-area" />
                    </div>
                )}
                {step === 5 && (
                    <div data-testid="step-preferences">
                        <div className="overline">Step 5</div>
                        <h1 className="mt-3 font-display text-4xl tracking-tight">Currency & measurement.</h1>
                        <div className="mt-8 grid md:grid-cols-2 gap-6">
                            <div>
                                <Label>Currency</Label>
                                <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className="mt-2 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="select-currency">
                                    {["USD", "CAD", "EUR", "GBP", "AUD", "MXN"].map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label>Measurement</Label>
                                <select value={form.measurement_system} onChange={(e) => set("measurement_system", e.target.value)} className="mt-2 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="select-measurement">
                                    <option value="imperial">Imperial (ft / in / lb)</option>
                                    <option value="metric">Metric (m / kg)</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
                {step === 6 && (
                    <div data-testid="step-brand">
                        <div className="overline">Step 6</div>
                        <h1 className="mt-3 font-display text-4xl tracking-tight">Name your organization.</h1>
                        <p className="mt-3 text-sm text-muted-foreground">You can leave the placeholder in place if you'd like to rename later from Settings → Branding.</p>
                        <Label className="mt-8 block">Organization / company name</Label>
                        <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="The Dream Forge Company" className="mt-2" data-testid="input-org-name" />
                        <Label className="mt-6 block">Logo URL (optional)</Label>
                        <Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://…/logo.png" className="mt-2" data-testid="input-logo-url" />

                        <div className="mt-8 border border-border p-6">
                            <div className="overline">Workspace</div>
                            <div className="mt-4 grid md:grid-cols-2 gap-3">
                                {[["clean", "Start with a clean workspace"], ["sample", "Explore with sample data (R2)"]].map(([k, l]) => (
                                    <label key={k} className={`flex items-center gap-3 p-4 border cursor-pointer ${form.workspace_choice === k ? "border-foreground bg-secondary/50" : "border-border"} ${k === "sample" ? "opacity-50" : ""}`} data-testid={`workspace-${k}`}>
                                        <input type="radio" checked={form.workspace_choice === k} onChange={() => k === "clean" && set("workspace_choice", k)} disabled={k === "sample"} />
                                        <span>{l}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-12 flex items-center justify-between">
                    <button type="button" onClick={back} disabled={step === 1} className="text-sm text-muted-foreground disabled:opacity-40 hover:opacity-70" data-testid="onboarding-back">← Back</button>
                    {step < total ? (
                        <button type="button" onClick={next} disabled={!canProceed()} className="bg-foreground text-background px-6 py-3 text-sm hover:opacity-90 disabled:opacity-50" data-testid="onboarding-next">Continue →</button>
                    ) : (
                        <button type="button" onClick={submit} disabled={!canProceed() || saving} className="bg-navy text-arch-white px-6 py-3 text-sm hover:opacity-90 disabled:opacity-50" data-testid="onboarding-finish">{saving ? "Creating…" : "Create organization"}</button>
                    )}
                </div>
            </div>
        </div>
    );
}
